import React, { useState, useEffect } from "react";
import {
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  ThemeProvider,
} from "@material-tailwind/react";
import SearchCard, { type Course } from "../cards/SearchCard";
import { theme } from "../../constants/themes";
import { browseCourseTabsData } from "../../content/university-detail-page";
import { ArrowRightAltIcon } from "../../common/icons";
import {
  UniversityIcon,
  ShareIcon,
  SaveIcon,
  ActionIcon,
  SaveIconActive,
} from "../../common/icons";
import { getAllSavedResources, searchCourseReq } from "../../utils/api";
import { courseSearchData } from "../../content/course-search-page";
import type { CourseData } from "../cards/CourseCard";
import CompareModal from "../../sections/modal-button/Compare";
import type { CourseItem } from "../../content/save-page-course";

const BrowseCourseTabs: React.FC<{ slug: string }> = ({ slug }) => {
  const [activeTab, setActiveTab] = useState<string>("bachelors");
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [savedCourseIds, setSavedCourseIds] = useState<Set<number>>(new Set());
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<Course[]>([]);
  const [refresh, setRefresh] = useState(false);

  const { defaultImage, labels } = courseSearchData;

  // Fetch saved courses
  async function getSavedCourses() {
    try {
      const response = await getAllSavedResources("course");
      const savedIds: Set<number> = new Set(
        response.courses.map((course: CourseItem) => course.course.id),
      );
      setSavedCourseIds(savedIds);
    } catch (error) {
      console.error("Error fetching saved courses:", error);
    }
  }

  // Fetch courses based on active tab and university slug
  const fetchCourses = async (degree_level: string, page = 1) => {
    try {
      setLoading(true);
      const params = {
        degreelevel: degree_level,
        institute: slug,
        page: page.toString(),
      };
      const response = await searchCourseReq(params);
      setCourses(response.result);
    } catch (error) {
      console.error("Error fetching courses:", error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle save and remove
  const handleRemove = () => {
    setRefresh((prev) => !prev);
  };

  const handleSave = () => {
    setRefresh((prev) => !prev);
  };

  // Refresh saved courses when needed
  useEffect(() => {
    getSavedCourses();
  }, []);

  useEffect(() => {
    if (refresh) {
      getSavedCourses();
      setRefresh(false);
    }
  }, [refresh]);

  // Fetch courses on component mount and when activeTab changes
  useEffect(() => {
    if (slug) {
      fetchCourses(activeTab);
    }
  }, [activeTab, slug]);

  // Handle tab change
  const handleTabChange = (newValue: string) => {
    setActiveTab(newValue);
    setCourses([]);
  };

  // Course comparison handler
  const handleCompareClick = (newCourse: Course) => {
    const currentSelectedCourses = [...selectedCourses];

    if (currentSelectedCourses.length >= 3) {
      alert("You can compare maximum 3 courses");
      return;
    }

    if (currentSelectedCourses.some((course) => course.id === newCourse.id)) {
      alert("This course is already selected for comparison");
      return;
    }

    const updatedCourses = [...currentSelectedCourses, newCourse];
    setSelectedCourses(updatedCourses);
    setIsCompareModalOpen(true);
  };

  // Generate card configuration
  const getCardConfig = (course: CourseData) => {
    const isAlreadySaved =
      course.course_id !== undefined && savedCourseIds.has(course.course_id);

    return {
      icon: UniversityIcon,
      pageType: "course" as const,
      detailsConfig: [
        {
          label: labels.degree,
          value: course.degreelevel_name,
          key: "degree",
        },
        {
          label: labels.uniRating,
          value: course.qs_ranking || "N/A",
          key: "rating",
        },
        {
          label: labels.studyMode,
          value: course.studyMode || "N/A",
          key: "mode",
        },
        {
          label: labels.fee,
          value:
            course.course_fee === 0
              ? "0 " + (course.currency || " ")
              : course.course_fee === null
                ? "To be confirmed"
                : course.currency + " " + course.course_fee,
          key: "fee",
        },
        {
          label: labels.language,
          value: course.course_language || "N/A",
          key: "language",
        },
        {
          label: labels.duration,
          value: course.duration || "N/A",
          key: "duration",
        },
      ],
      buttons: {
        primary: {
          label: "Apply now",
          href: "https://timesconsultant.com/contact/",
        },
        secondary: {
          label: labels.viewDetail,
          href: `/course/${course.course_slug}/${course.duration_id}/${course.course_fee_id}`,
        },
      },
      actionIcons: [
        isAlreadySaved ? SaveIconActive : SaveIcon,
        ActionIcon,
        ShareIcon,
      ],
    };
  };

  // Navigate to full course search
  const handleSeeAllClick = () => {
    const params = new URLSearchParams({
      institute: slug,
      degreelevel: activeTab,
    });
    window.location.href = `/course-search?${params.toString()}`;
  };
  const displayCourses = courses.length > 3 ? courses.slice(0, 3) : courses;
  return (
    <ThemeProvider value={theme}>
      <Tabs value={activeTab} onChange={(tab: string) => handleTabChange(tab)}>
        <TabsHeader>
          {browseCourseTabsData.map(({ label, value }) => (
            <Tab
              key={value}
              value={value}
              onClick={() => handleTabChange(value)}
            >
              {label}
            </Tab>
          ))}
        </TabsHeader>
        <TabsBody>
          <TabPanel key={activeTab} value={activeTab}>
            {loading && courses.length === 0 ? (
              <p>Loading courses...</p>
            ) : displayCourses.length > 0 ? (
              <>
                {displayCourses.map((course, index) => (
                  <SearchCard
                    key={index}
                    data={{
                      name: course.name,
                      institute_name: course.institute_name,
                      logo: course.logo,
                      fee: course.course_fee,
                      course_slug: course.course_slug,
                      duration_id: course.duration_id,
                      course_fee_id: course.course_fee_id,
                      institute_slug: course.institute_slug,
                      id: course.course_id,
                      type: "course",
                    }}
                    config={getCardConfig(course)}
                    defaultImage={defaultImage}
                    gridClassName="grid-cols-4 xl:grid-cols-6"
                    removeBorderAfterMobileFourth={true}
                    handleCompareClick={handleCompareClick}
                    isAlreadySaved={
                      course.course_id !== undefined &&
                      savedCourseIds.has(course.course_id)
                    }
                    onRemove={handleRemove}
                    onSave={handleSave}
                    variant="uni-detail"
                  />
                ))}

                {courses.length > 5 && (
                  <span
                    className="flex px-2 md:px-4 justify-end items-center cursor-pointer text-primary"
                    onClick={handleSeeAllClick}
                  >
                    <ArrowRightAltIcon />
                    See all
                  </span>
                )}
              </>
            ) : (
              <p>No courses available for this tab.</p>
            )}
          </TabPanel>
        </TabsBody>
      </Tabs>

      <CompareModal
        open={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        selectedEntities={selectedCourses}
        entityType="course"
        setSelectedEntities={setSelectedCourses}
      />
    </ThemeProvider>
  );
};

export default BrowseCourseTabs;
